import { FunctionComponent, PropsWithChildren } from 'react';
import {
    Card,
    CardActions,
    CardContent,
    Stack,
    Typography,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { useGeneralProductConfigurationContext } from '@/pageContents/productConfiguration/ProductConfigurationDetail/ProductConfigurationGeneralConfiguration';
import {
    usePostProductConfigurationMutation,
    usePutProductConfigurationMutation,
} from '@/api/v2/productConfigurations';
import { useNotification } from '@starjack/features/notifications';
import { useAuthentication } from '@/hooks/useAuthentication';

type SectionCardProps = {
    title: string;
    hideActions?: boolean;
};

export const SectionCard: FunctionComponent<
    PropsWithChildren<SectionCardProps>
> = ({ title, hideActions = false, children }) => {
    const {
        liftOperatorData: { liftOperatorId },
    } = useAuthentication();
    const { productConfiguration } = useGeneralProductConfigurationContext();
    const [postProductConfiguration, { isLoading: postLoading }] =
        usePostProductConfigurationMutation();
    const [putProductConfiguration, { isLoading: putLoading }] =
        usePutProductConfigurationMutation();
    const { addNotification } = useNotification();

    const onClick = () => {
        const action = productConfiguration.id
            ? putProductConfiguration
            : postProductConfiguration;

        action({ liftOperatorId, productConfiguration })
            .unwrap()
            .then(() => {
                addNotification({
                    message: 'Konfiguration erfolgreich gespeichert',
                    type: 'success',
                });
            })
            .catch(() => {
                addNotification({
                    message:
                        'Fehler beim Speichern. Bitte versuchen Sie es später noch einmal',
                    type: 'error',
                });
            });
    };

    const isLoading = postLoading || putLoading;
    return (
        <Card>
            <CardContent
                sx={(theme) => ({
                    color: theme.palette.getContrastText(
                        theme.palette.primary.dark
                    ),
                    bgcolor: theme.palette.primary.dark,
                })}
            >
                <Typography variant='h6'>{title}</Typography>
            </CardContent>
            <CardContent>
                <Stack spacing={5}>{children}</Stack>
            </CardContent>

            {!hideActions && (
                <CardActions
                    sx={{
                        p: 4,
                    }}
                >
                    <LoadingButton
                        loading={isLoading}
                        onClick={onClick}
                        variant='contained'
                    >
                        Speichern
                    </LoadingButton>
                </CardActions>
            )}
        </Card>
    );
};
